import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Persist a form's state to localStorage so the user doesn't lose work
 * if their session expires, they refresh, or accidentally close the tab.
 *
 * - `key` should be unique per form (and ideally per user/route)
 * - `initialValue` is used when no draft exists
 * - Returns: [value, setValue, { clearDraft, hasDraft, savedAt }]
 *
 * File/Blob fields are NOT persisted (localStorage can't store them safely).
 * Pass `excludeKeys` to skip any other transient fields.
 */
export default function useFormDraft(key, initialValue, { excludeKeys = [], debounceMs = 400 } = {}) {
  const storageKey = `marema_draft_${key}`;
  const [hasDraft, setHasDraft] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const loadInitial = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return initialValue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'value' in parsed) {
        return { ...initialValue, ...parsed.value };
      }
    } catch (_) {
      /* ignore */
    }
    return initialValue;
  };

  const [value, setValue] = useState(loadInitial);

  // Mark hasDraft after mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setHasDraft(true);
        setSavedAt(parsed?.savedAt || null);
      }
    } catch (_) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timerRef = useRef(null);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        const cleaned = {};
        for (const k of Object.keys(value || {})) {
          if (excludeKeys.includes(k)) continue;
          const v = value[k];
          if (v instanceof File || v instanceof Blob) continue;
          cleaned[k] = v;
        }
        const now = new Date().toISOString();
        localStorage.setItem(storageKey, JSON.stringify({ value: cleaned, savedAt: now }));
        setSavedAt(now);
        setHasDraft(true);
      } catch (_) {
        /* ignore */
      }
    }, debounceMs);

    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (_) {
      /* ignore */
    }
    setHasDraft(false);
    setSavedAt(null);
  }, [storageKey]);

  return [value, setValue, { clearDraft, hasDraft, savedAt }];
}
